import { useState } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { X, Save, Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import SignaturePad from './SignaturePad';
import { jsPDF } from 'jspdf';
import TrimbleAddressAutocomplete from '@/components/TrimbleAddressAutocomplete';

const inputCls = "w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50";
const labelCls = "text-xs text-muted-foreground block mb-1";
const selectCls = "w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/50";

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const SectionHeader = ({ title }) => (
  <div className="bg-white/5 px-3 py-1.5 rounded text-xs font-bold text-foreground uppercase tracking-wider mt-4 mb-3">
    {title}
  </div>
);

const Field = ({ label, required, children }) => (
  <div>
    <label className={labelCls}>{label}{required && <span className="text-red-400 ml-0.5">*</span>}</label>
    {children}
  </div>
);

const YesNo = ({ value, onChange }) => (
  <div className="flex items-center gap-4">
    {['No', 'Yes'].map(opt => (
      <label key={opt} className="flex items-center gap-2 cursor-pointer">
        <div className={cn(
          "w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors",
          value === opt ? 'border-primary bg-primary' : 'border-border'
        )}>
          {value === opt && <div className="w-1.5 h-1.5 rounded-full bg-primary-foreground" />}
        </div>
        <input type="radio" className="hidden" checked={value === opt} onChange={() => onChange(opt)} />
        <span className="text-sm text-foreground">{opt}</span>
      </label>
    ))}
  </div>
);

const emptyEmployer = () => ({
  name: '', address: '', city: '', state: '', zip: '',
  phone: '', fax: '', email: '', position: '',
  date_from: '', date_to: '', reason: '',
  dot_regulated: 'Yes', safety_sensitive: 'Yes', gaps: '',
});

export default function DriverApplicationModal({ driver, onClose, onSaved }) {
  const nameParts = (driver.full_name || '').trim().split(' ');
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  const addrParts = (driver.address || '').split(',');
  const street = (addrParts[0] || '').trim();
  const cityPart = (addrParts[1] || '').trim();
  const stateZipPart = (addrParts[2] || '').trim().split(' ');
  const addrState = stateZipPart[0] || driver.cdl_state || '';
  const addrZip = stateZipPart[1] || '';

  const [form, setForm] = useState({
    app_date: new Date().toISOString().split('T')[0],
    first_name: firstName,
    last_name: lastName,
    dob: '',
    ssn: driver.ein || '',
    phone: driver.phone || '',
    email: driver.user_email || '',
    address: street,
    city: cityPart,
    state: addrState,
    zip: addrZip,
    twic: 'No',
    passport: 'No',
    prev_address: '',
    prev_city: '',
    prev_state: '',
    prev_zip: '',
    lic_first: firstName,
    lic_last: lastName,
    lic_issued: '',
    lic_expires: driver.cdl_expiration_date || '',
    lic_state: driver.cdl_state || addrState,
    lic_class: 'US Class A',
    lic_number: driver.cdl_number || '',
    lic_endorsements: '',
    accidents: 'No',
    violations: 'No',
    denied_license: 'No',
    revoked_license: 'No',
    forfeiture_explain: '',
    signature: '',
    sign_date: new Date().toISOString().split('T')[0],
  });

  const [employers, setEmployers] = useState([emptyEmployer()]);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const updateEmployer = (i, k, v) => {
    setEmployers(prev => prev.map((e, idx) => idx === i ? { ...e, [k]: v } : e));
  };

  const addEmployer = () => setEmployers(prev => [...prev, emptyEmployer()]);
  const removeEmployer = (i) => setEmployers(prev => prev.filter((_, idx) => idx !== i));

  const generatePdf = async () => {
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const W = 612, ml = 50, mr = 562, lh = 14;
    let y = 50;

    const drawLine = (text, x, yy, opts = {}) => {
      doc.setFontSize(opts.size || 9);
      doc.setFont('helvetica', opts.bold ? 'bold' : 'normal');
      if (opts.color) doc.setTextColor(...opts.color);
      else doc.setTextColor(0, 0, 0);
      doc.text(String(text || '—'), x, yy);
    };

    const underlined = (label, value, x, yy, w) => {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const labelText = `${label}: `;
      doc.text(labelText, x, yy);
      const lw = doc.getTextWidth(labelText);
      doc.text(String(value || ''), x + lw, yy);
      doc.line(x + lw, yy + 1, x + lw + (w || 120), yy + 1);
    };

    const sectionBg = (yy, h = 16) => {
      doc.setFillColor(230, 230, 230);
      doc.rect(ml, yy - 11, mr - ml, h, 'F');
    };

    const radioRow = (question, value, x, yy) => {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(question, x, yy);
      doc.circle(x + 310, yy - 3, 4, 'S');
      doc.text('No', x + 318, yy);
      if (value === 'No') doc.circle(x + 310, yy - 3, 2, 'F');
      doc.circle(x + 340, yy - 3, 4, 'S');
      doc.text('Yes', x + 348, yy);
      if (value === 'Yes') doc.circle(x + 340, yy - 3, 2, 'F');
    };

    // Header
    drawLine('GF Trucklines LLC', ml, y, { bold: true, size: 12 });
    doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(0,0,0);
    doc.text('GF Trucklines LLC', mr - 100, y); y += lh;
    doc.text('6041 Thorne Bay Ct', mr - 100, y); y += lh;
    doc.text('Las Vegas, NV 89110', mr - 100, y); y += lh;
    doc.text('(702) 601-8300', mr - 100, y); y += 30;

    doc.setFontSize(14); doc.setFont('helvetica', 'bold'); doc.setTextColor(0,0,0);
    doc.text('APPLICATION', W / 2, y, { align: 'center' }); y += 25;

    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    underlined('Application Date', form.app_date, ml, y, 100);
    underlined('Company Name', 'GF Trucklines LLC', ml + 230, y, 120); y += lh + 4;
    underlined('Address', '6041 Thorne Bay Ct', ml, y, 120);
    underlined('City', 'Las Vegas', ml + 230, y, 100); y += lh + 4;
    underlined('State', 'NV', ml, y, 30);
    underlined('Postal Code', '89110', ml + 80, y, 80); y += 20;

    sectionBg(y); drawLine('APPLICANT INFORMATION', ml + 2, y, { bold: true, size: 9 }); y += 18;
    underlined('First Name', form.first_name, ml, y, 120);
    underlined('Last Name', form.last_name, ml + 230, y, 120); y += lh + 4;
    underlined('Date of Birth', form.dob, ml, y, 100);
    underlined('Social Security Number', form.ssn, ml + 230, y, 100); y += lh + 4;
    underlined('Main Phone', form.phone, ml, y, 120);
    underlined('Email', form.email, ml + 230, y, 150); y += lh + 4;
    underlined('Address', form.address, ml, y, 150);
    underlined('City', form.city, ml + 230, y, 100); y += lh + 4;
    underlined('State', form.state, ml, y, 30);
    underlined('Zipcode', form.zip, ml + 80, y, 80); y += lh + 4;
    doc.setTextColor(0,0,0);
    doc.text(`Do you have a TWIC Card?: ${form.twic}`, ml, y);
    doc.text(`Do you have a passport?: ${form.passport}`, ml + 230, y); y += 20;

    sectionBg(y); drawLine('RESIDENCES PREVIOUS 3 YEARS', ml + 2, y, { bold: true, size: 9 }); y += 14;
    doc.setFontSize(9); doc.setTextColor(0,0,0);
    doc.text('List residence for previous 3 years if you lived at the above address less than 3 years.', ml, y); y += 14;
    underlined('Address', form.prev_address, ml, y, 150);
    underlined('City', form.prev_city, ml + 230, y, 100); y += lh + 4;
    underlined('State', form.prev_state, ml, y, 30);
    underlined('Postal Code', form.prev_zip, ml + 80, y, 80); y += 20;

    sectionBg(y); drawLine('DRIVERS LICENSE INFORMATION', ml + 2, y, { bold: true, size: 9 }); y += 14;
    doc.setFontSize(8); doc.setTextColor(0,0,0);
    doc.text("No person who operates a commercial motor vehicle shall at any time have more than one driver's license (49 CFR 383.21).", ml, y); y += 11;
    doc.text('I certify that I do not have more than one motor vehicle license, the information for which is listed below.', ml, y); y += 11;
    doc.setFontSize(9);
    doc.text('List all driver licenses held within the last 3 years. Enter your first and last name exactly as it appears on your license.', ml, y); y += 14;
    underlined('First Name', form.lic_first, ml, y, 100);
    underlined('Last Name', form.lic_last, ml + 160, y, 100);
    underlined('Issued', form.lic_issued, ml + 320, y, 80);
    underlined('Expires', form.lic_expires, ml + 430, y, 80); y += lh + 4;
    underlined('State', form.lic_state, ml, y, 30);
    underlined('Class', form.lic_class, ml + 80, y, 80);
    underlined('License Number', form.lic_number, ml + 200, y, 120); y += lh + 4;
    underlined('Endorsements', form.lic_endorsements, ml, y, 200); y += 20;

    sectionBg(y); drawLine('EXPERIENCE', ml + 2, y, { bold: true, size: 9 }); y += 18;
    sectionBg(y); drawLine('ACCIDENTS/CRASHES PREVIOUS 3 YEARS', ml + 2, y, { bold: true, size: 9 }); y += 16;
    radioRow('Have you had any accidents/crashes in the last 3 years?', form.accidents, ml, y); y += 18;
    sectionBg(y); drawLine('MOVING TRAFFIC VIOLATIONS PREVIOUS 3 YEARS', ml + 2, y, { bold: true, size: 9 }); y += 16;
    radioRow('Have you had any traffic violations in the last 3 years?', form.violations, ml, y); y += 20;

    doc.addPage(); y = 50;

    sectionBg(y); drawLine('FORFEITURES PREVIOUS 3 YEARS', ml + 2, y, { bold: true, size: 9 }); y += 18;
    radioRow('A. Have you ever been denied a license, permit or privilege to operate a motor vehicle?', form.denied_license, ml, y); y += 16;
    radioRow('B. Has any license, permit or privilege ever been revoked?', form.revoked_license, ml, y); y += 14;
    doc.setTextColor(0,0,0);
    doc.text('C. If yes to either question above, briefly describe the circumstances.', ml, y); y += 12;
    doc.text(form.forfeiture_explain || '', ml, y); y += 20;

    sectionBg(y); drawLine('EMPLOYMENT RECORD PREVIOUS 3 YEARS', ml + 2, y, { bold: true, size: 9 }); y += 14;
    doc.setTextColor(200, 0, 0);
    doc.text('List all employers for the previous 3 years and an additional 7 years if you were employed as a DRIVER', ml, y);
    doc.setTextColor(0, 0, 0); y += 18;

    for (const emp of employers) {
      if (y > 680) { doc.addPage(); y = 50; }
      underlined('Employer Name', emp.name, ml, y, 130);
      underlined('Address', emp.address, ml + 230, y, 120); y += lh + 4;
      underlined('City', emp.city, ml, y, 100);
      underlined('State', emp.state, ml + 150, y, 30);
      underlined('Postal Code', emp.zip, ml + 210, y, 80); y += lh + 4;
      underlined('Phone', emp.phone, ml, y, 100);
      underlined('Fax', emp.fax, ml + 160, y, 80);
      underlined('Email', emp.email, ml + 270, y, 120); y += lh + 4;
      underlined('Position Held', emp.position, ml, y, 120);
      underlined('Date From', emp.date_from, ml + 200, y, 80);
      underlined('Date To', emp.date_to, ml + 330, y, 80); y += lh + 4;
      underlined('Reason For Leaving', emp.reason, ml, y, 200); y += lh + 6;
      doc.setTextColor(0,0,0);
      doc.text('Were you subject to the DOT/FMCSA regulations while employed by this carrier?', ml, y); y += 12;
      doc.setFont('helvetica', 'bold'); doc.text('If you were a driver, choose Yes', ml, y); doc.setFont('helvetica', 'normal');
      doc.circle(ml + 195, y - 3, 4, 'S'); doc.text('No', ml + 203, y);
      if (emp.dot_regulated === 'No') doc.circle(ml + 195, y - 3, 2, 'F');
      doc.circle(ml + 230, y - 3, 4, 'S'); doc.text('Yes', ml + 238, y);
      if (emp.dot_regulated === 'Yes') doc.circle(ml + 230, y - 3, 2, 'F'); y += 14;
      doc.text('Was your job designated as a safety sensitive function in any DOT regulated mode, subject to the alcohol and', ml, y); y += 11;
      doc.text('controlled substances testing requirements required by 49 CFR Part 40?', ml, y); y += 12;
      doc.setFont('helvetica', 'bold'); doc.text('If you were a driver, choose Yes', ml, y); doc.setFont('helvetica', 'normal');
      doc.circle(ml + 195, y - 3, 4, 'S'); doc.text('No', ml + 203, y);
      if (emp.safety_sensitive === 'No') doc.circle(ml + 195, y - 3, 2, 'F');
      doc.circle(ml + 230, y - 3, 4, 'S'); doc.text('Yes', ml + 238, y);
      if (emp.safety_sensitive === 'Yes') doc.circle(ml + 230, y - 3, 2, 'F'); y += 14;
      doc.text('Explain any gaps in employment if in excess of one (1) month. Include month/year and reason.', ml, y); y += 11;
      if (emp.gaps) doc.text(emp.gaps, ml, y);
      doc.line(ml, y + 2, mr, y + 2); y += 20;
    }

    doc.addPage(); y = 50;
    sectionBg(y); drawLine('To Be Read And Signed By Applicant', ml + 2, y, { bold: true, size: 9 }); y += 20;
    doc.setFontSize(8.5); doc.setTextColor(0,0,0);
    const authText = [
      'I authorize you to make investigations (including contacting current and prior employers) into my personal,',
      'employment, financial, medical history, and other related matters as may be necessary in arriving at an employment',
      'decision. I hereby release employers, schools, health care providers, and other persons from all liability in responding',
      'to inquiries and releasing information in connection with my application.',
      '',
      'In the event of employment, I understand that false or misleading information given in my application or interview(s)',
      'may result in discharge. I also understand that I am required to abide by all rules and regulations of the Company.',
      '',
      'I understand that the information I provide regarding my current and/or prior employers may be used, and those',
      "employer(s) will be contacted for the purpose of investigating my safety performance history as required by 49 CFR",
      '391.23. I understand that I have the right to:',
      '',
      '    Review information provided by current/previous employers;',
      '    Have errors in the information corrected by previous employers, and for those previous employers to resend the',
      '    corrected information to the prospective employer; and',
      '    Have a rebuttal statement attached to the alleged erroneous information, if the previous employer(s) and I cannot',
      '    agree on the accuracy of the information.',
      '',
      'This certifies that I completed this application, and that all entries on it and information in it are true and complete to',
      'the best of my knowledge. Note: A motor carrier may require an applicant to provide more information than that',
      'required by the Federal Motor Carrier Safety Regulations.',
    ];
    for (const t of authText) { doc.text(t, ml, y); y += 12; }

    y += 10;
    if (form.signature && form.signature.startsWith('data:')) {
      doc.addImage(form.signature, 'PNG', ml, y, 100, 40);
    }
    doc.line(ml, y + 45, ml + 150, y + 45);
    doc.setTextColor(0,0,0);
    doc.text('Applicant Signature', ml, y + 56);
    doc.text(`Date Signed: ${form.sign_date}`, ml + 230, y + 50);

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8); doc.setTextColor(100);
      doc.text(new Date().toLocaleDateString(), ml, 780);
      doc.text('GF Trucklines LLC', W / 2, 780, { align: 'center' });
      doc.text(`Page ${i}`, mr, 780, { align: 'right' });
    }

    return doc;
  };

  const handleSaveAndUpload = async () => {
    if (!form.signature) {
      toast.error('Please provide a signature before saving.');
      return;
    }
    setSaving(true);
    toast.info('Generating application PDF...');
    const doc = await generatePdf();
    const blob = doc.output('blob');
    const file = new File([blob], `driver_application_${(driver.full_name || 'driver').replace(/\s+/g, '_')}.pdf`, { type: 'application/pdf' });
    const { file_url } = await client.integrations.Core.UploadFile({ file });
    await client.entities.DriverProfile.update(driver.id, {
      driver_application_url: file_url,
      dqf_driver_application: true,
    });
    toast.success('Driver Application saved and marked complete!');
    setSaving(false);
    if (onSaved) onSaved(file_url);
    onClose();
  };

  const handleDownload = async () => {
    const doc = await generatePdf();
    doc.save(`driver_application_${(driver.full_name || 'driver').replace(/\s+/g, '_')}.pdf`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-3xl z-10 max-h-[92vh] flex flex-col overflow-hidden">

        <div className="flex items-start justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div>
            <h2 className="font-space font-bold text-foreground text-lg">Driver's Application for Employment</h2>
            <p className="text-xs text-muted-foreground mt-0.5">49 CFR 391.21 — Pre-filled from driver profile</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground mt-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-6 space-y-4 flex-1 text-sm">

          <div className="grid grid-cols-2 gap-4">
            <Field label="Application Date" required>
              <input type="date" value={form.app_date} onChange={e => set('app_date', e.target.value)} className={inputCls} />
            </Field>
          </div>

          <SectionHeader title="Applicant Information" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="First Name" required><input value={form.first_name} onChange={e => set('first_name', e.target.value)} className={inputCls} /></Field>
            <Field label="Last Name" required><input value={form.last_name} onChange={e => set('last_name', e.target.value)} className={inputCls} /></Field>
            <Field label="Date of Birth"><input type="date" value={form.dob} onChange={e => set('dob', e.target.value)} className={inputCls} /></Field>
            <Field label="Social Security Number"><input value={form.ssn} onChange={e => set('ssn', e.target.value)} className={inputCls} placeholder="xxx-xx-0000" /></Field>
            <Field label="Main Phone"><input value={form.phone} onChange={e => set('phone', e.target.value)} className={inputCls} /></Field>
            <Field label="Email"><input value={form.email} onChange={e => set('email', e.target.value)} className={inputCls} /></Field>
            <TrimbleAddressAutocomplete
              value={form.address}
              onChange={e => set('address', e.target.value)}
              onSelect={data => {
                set('address', data.street || form.address);
                if (data.city) set('city', data.city);
                if (data.state) set('state', data.state);
                if (data.zip) set('zip', data.zip);
              }}
              label="Address"
            />
            <Field label="City"><input value={form.city} onChange={e => set('city', e.target.value)} className={inputCls} /></Field>
            <Field label="State">
              <select value={form.state} onChange={e => set('state', e.target.value)} className={selectCls}>
                <option value="" className="bg-background">Select…</option>
                {US_STATES.map(s => <option key={s} value={s} className="bg-background">{s}</option>)}
              </select>
            </Field>
            <Field label="Zip Code"><input value={form.zip} onChange={e => set('zip', e.target.value)} className={inputCls} /></Field>
            <div>
              <label className={labelCls}>Do you have a TWIC Card?</label>
              <YesNo value={form.twic} onChange={v => set('twic', v)} />
            </div>
            <div>
              <label className={labelCls}>Do you have a passport?</label>
              <YesNo value={form.passport} onChange={v => set('passport', v)} />
            </div>
          </div>

          <SectionHeader title="Residences Previous 3 Years" />
          <p className="text-xs text-muted-foreground">List residence for previous 3 years if you lived at the above address less than 3 years.</p>
          <div className="grid grid-cols-2 gap-4">
            <TrimbleAddressAutocomplete
              value={form.prev_address}
              onChange={e => set('prev_address', e.target.value)}
              onSelect={data => {
                set('prev_address', data.street || form.prev_address);
                if (data.city) set('prev_city', data.city);
                if (data.state) set('prev_state', data.state);
                if (data.zip) set('prev_zip', data.zip);
              }}
              label="Address"
            />
            <Field label="City"><input value={form.prev_city} onChange={e => set('prev_city', e.target.value)} className={inputCls} /></Field>
            <Field label="State">
              <select value={form.prev_state} onChange={e => set('prev_state', e.target.value)} className={selectCls}>
                <option value="" className="bg-background">Select…</option>
                {US_STATES.map(s => <option key={s} value={s} className="bg-background">{s}</option>)}
              </select>
            </Field>
            <Field label="Postal Code"><input value={form.prev_zip} onChange={e => set('prev_zip', e.target.value)} className={inputCls} /></Field>
          </div>

          <SectionHeader title="Drivers License Information" />
          <p className="text-xs text-muted-foreground">List all driver licenses held within the last 3 years.</p>
          <div className="grid grid-cols-2 gap-4">
            <Field label="First Name"><input value={form.lic_first} onChange={e => set('lic_first', e.target.value)} className={inputCls} /></Field>
            <Field label="Last Name"><input value={form.lic_last} onChange={e => set('lic_last', e.target.value)} className={inputCls} /></Field>
            <Field label="Issued Date"><input type="date" value={form.lic_issued} onChange={e => set('lic_issued', e.target.value)} className={inputCls} /></Field>
            <Field label="Expires"><input type="date" value={form.lic_expires} onChange={e => set('lic_expires', e.target.value)} className={inputCls} /></Field>
            <Field label="State">
              <select value={form.lic_state} onChange={e => set('lic_state', e.target.value)} className={selectCls}>
                <option value="" className="bg-background">Select…</option>
                {US_STATES.map(s => <option key={s} value={s} className="bg-background">{s}</option>)}
              </select>
            </Field>
            <Field label="Class"><input value={form.lic_class} onChange={e => set('lic_class', e.target.value)} className={inputCls} /></Field>
            <Field label="License Number"><input value={form.lic_number} onChange={e => set('lic_number', e.target.value)} className={inputCls} /></Field>
            <Field label="Endorsements"><input value={form.lic_endorsements} onChange={e => set('lic_endorsements', e.target.value)} className={inputCls} placeholder="e.g. H, N, T" /></Field>
          </div>

          <SectionHeader title="Experience" />
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Have you had any accidents/crashes in the last 3 years?</label>
              <YesNo value={form.accidents} onChange={v => set('accidents', v)} />
            </div>
            <div>
              <label className={labelCls}>Have you had any traffic violations in the last 3 years?</label>
              <YesNo value={form.violations} onChange={v => set('violations', v)} />
            </div>
          </div>

          <SectionHeader title="Forfeitures Previous 3 Years" />
          <div className="space-y-3">
            <div>
              <label className={labelCls}>A. Have you ever been denied a license, permit or privilege to operate a motor vehicle?</label>
              <YesNo value={form.denied_license} onChange={v => set('denied_license', v)} />
            </div>
            <div>
              <label className={labelCls}>B. Has any license, permit or privilege ever been revoked?</label>
              <YesNo value={form.revoked_license} onChange={v => set('revoked_license', v)} />
            </div>
            {(form.denied_license === 'Yes' || form.revoked_license === 'Yes') && (
              <Field label="C. If yes, briefly describe the circumstances">
                <textarea value={form.forfeiture_explain} onChange={e => set('forfeiture_explain', e.target.value)} rows={2} className={inputCls + ' resize-none'} />
              </Field>
            )}
          </div>

          <SectionHeader title="Employment Record Previous 3 Years" />
          <p className="text-xs text-red-400 font-medium">List all employers for the previous 3 years and an additional 7 years if you were employed as a DRIVER</p>

          {employers.map((emp, i) => (
            <div key={i} className="rounded-xl border border-border bg-muted/40 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-primary uppercase tracking-wider">Employer {i + 1}</p>
                {employers.length > 1 && (
                  <button type="button" onClick={() => removeEmployer(i)} className="text-red-400 hover:text-red-300 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Employer Name"><input value={emp.name} onChange={e => updateEmployer(i, 'name', e.target.value)} className={inputCls} /></Field>
                <TrimbleAddressAutocomplete
                  value={emp.address}
                  onChange={e => updateEmployer(i, 'address', e.target.value)}
                  onSelect={data => {
                    updateEmployer(i, 'address', data.street || emp.address);
                    if (data.city) updateEmployer(i, 'city', data.city);
                    if (data.state) updateEmployer(i, 'state', data.state);
                    if (data.zip) updateEmployer(i, 'zip', data.zip);
                  }}
                  label="Address"
                />
                <Field label="City"><input value={emp.city} onChange={e => updateEmployer(i, 'city', e.target.value)} className={inputCls} /></Field>
                <Field label="State">
                  <select value={emp.state} onChange={e => updateEmployer(i, 'state', e.target.value)} className={selectCls}>
                    <option value="" className="bg-background">Select…</option>
                    {US_STATES.map(s => <option key={s} value={s} className="bg-background">{s}</option>)}
                  </select>
                </Field>
                <Field label="Zip"><input value={emp.zip} onChange={e => updateEmployer(i, 'zip', e.target.value)} className={inputCls} /></Field>
                <Field label="Phone"><input value={emp.phone} onChange={e => updateEmployer(i, 'phone', e.target.value)} className={inputCls} /></Field>
                <Field label="Fax"><input value={emp.fax} onChange={e => updateEmployer(i, 'fax', e.target.value)} className={inputCls} /></Field>
                <Field label="Email"><input value={emp.email} onChange={e => updateEmployer(i, 'email', e.target.value)} className={inputCls} /></Field>
                <Field label="Position Held"><input value={emp.position} onChange={e => updateEmployer(i, 'position', e.target.value)} className={inputCls} /></Field>
                <Field label="Date From (mm/yyyy)"><input value={emp.date_from} onChange={e => updateEmployer(i, 'date_from', e.target.value)} className={inputCls} placeholder="03/2025" /></Field>
                <Field label="Date To (mm/yyyy)"><input value={emp.date_to} onChange={e => updateEmployer(i, 'date_to', e.target.value)} className={inputCls} placeholder="02/2026" /></Field>
                <Field label="Reason For Leaving"><input value={emp.reason} onChange={e => updateEmployer(i, 'reason', e.target.value)} className={inputCls} /></Field>
              </div>
              <div className="space-y-2">
                <div>
                  <label className={labelCls}>Subject to DOT/FMCSA regulations? <span className="text-primary font-semibold">(If driver, choose Yes)</span></label>
                  <YesNo value={emp.dot_regulated} onChange={v => updateEmployer(i, 'dot_regulated', v)} />
                </div>
                <div>
                  <label className={labelCls}>Safety sensitive function subject to 49 CFR Part 40? <span className="text-primary font-semibold">(If driver, choose Yes)</span></label>
                  <YesNo value={emp.safety_sensitive} onChange={v => updateEmployer(i, 'safety_sensitive', v)} />
                </div>
                <Field label="Explain gaps in employment (if > 1 month)">
                  <textarea value={emp.gaps} onChange={e => updateEmployer(i, 'gaps', e.target.value)} rows={2} className={inputCls + ' resize-none'} />
                </Field>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={addEmployer}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-border text-xs text-muted-foreground hover:text-foreground hover:border-border transition-colors w-full justify-center"
          >
            <Plus className="w-3.5 h-3.5" /> Add Another Employer
          </button>

          <SectionHeader title="Signature" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            This certifies that I completed this application, and that all entries on it and information in it are true and complete to the best of my knowledge.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Applicant Signature <span className="text-red-400">*</span></label>
              <SignaturePad value={form.signature} onChange={v => set('signature', v)} />
            </div>
            <Field label="Date Signed">
              <input type="date" value={form.sign_date} onChange={e => set('sign_date', e.target.value)} className={inputCls} />
            </Field>
          </div>

        </div>

        <div className="px-6 py-4 border-t border-border flex justify-between items-center gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={handleDownload}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
          >
            Download PDF (preview)
          </button>
          <div className="flex gap-3">
            <Button variant="outline" className="border-border" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={handleSaveAndUpload} disabled={saving} className="bg-primary text-primary-foreground">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save & Upload</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}